import type { useLanguage } from "@/context/language"
import type { coworkAccountingReportTemplates } from "./report-templates"
import {
  validAccountCardSelection,
  type AccountCardSelection,
} from "./report-templates"

export const coworkReportOutputLanguages = ["app", "en", "ru", "et", "de", "fr", "es"] as const
export type CoworkReportOutputLanguage = (typeof coworkReportOutputLanguages)[number]
const coworkReportOutputLanguageKey = "cowork.agent.reportOutputLanguage"

export function storedCoworkReportOutputLanguage(): CoworkReportOutputLanguage {
  const value = localStorage.getItem(coworkReportOutputLanguageKey)
  return coworkReportOutputLanguages.includes(value as CoworkReportOutputLanguage)
    ? (value as CoworkReportOutputLanguage)
    : "app"
}

export function setStoredCoworkReportOutputLanguage(value: CoworkReportOutputLanguage) {
  localStorage.setItem(coworkReportOutputLanguageKey, value)
}

export function coworkReportOutputLanguageLabel(translate: (key: string) => string) {
  return translate(`cowork.agent.settings.reportLanguage.option.${storedCoworkReportOutputLanguage()}`)
}

export function coworkReportOutputLanguageInstructionValue(locale: string) {
  const value = storedCoworkReportOutputLanguage()
  if (value === "app") return `application language (${locale})`
  return (
    { en: "English", ru: "Russian", et: "Estonian", de: "German", fr: "French", es: "Spanish" } as Record<
      Exclude<CoworkReportOutputLanguage, "app">,
      string
    >
  )[value]
}

export function buildCoworkReportTemplatePrompt(
  template: (typeof coworkAccountingReportTemplates)[number],
  translate: ReturnType<typeof useLanguage>["t"],
  accountCard?: AccountCardSelection,
) {
  if (template.id === "account-card" && accountCard && !validAccountCardSelection(accountCard)) {
    throw new Error("Invalid account selection")
  }
  const selection = template.id === "account-card" && accountCard
    ? { ...accountCard, account_code: accountCard.account_code.trim() }
    : undefined
  return {
    instructions: `report_template_context: ${JSON.stringify({
      task: "generate_report",
      template_id: template.id,
      template_version: template.version,
      report_type: template.reportType,
      jurisdiction: template.country,
      ...(template.definition?.outputs.length
        ? { output_format: template.definition.outputs[0],
            ...(template.definition.outputs.length > 1 ? { output_formats: template.definition.outputs } : {}) }
        : {}),
      ...(selection ? { parameters: { account_card: selection } } : {}),
    })}`,
    visiblePrompt: selection
      ? translate(selection.include_subaccounts
          ? "cowork.agent.settings.template.accountCard.selectedPromptWithSubaccounts"
          : "cowork.agent.settings.template.accountCard.selectedPrompt", { account: selection.account_code })
      : translate(template.visiblePromptKey),
  }
}
